import { Component, OnInit, Inject } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Router, NavigationEnd } from "@angular/router";
import {
  faTicketAlt,
  faUsers,
  faHome,
  faChartBar,
  faCog,
  faCar,
  faPen,
  faCloudDownload,
  faFire,
  faClock,
  faComments,
  faUmbrellaBeach,
  faUserTie,
  faBicycle,
  faWrench,
  faMapMarkerAlt,
  faUserShield,
  faBriefcase,
  faTasks,
  faList,
  faMapMarker,
  faCity,
  faMapLocationDot,
  faHeart,
  faBuildingColumns,
} from "@fortawesome/free-solid-svg-icons";
import { AuthorizedUser } from "@app/core/models/authorized-user";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { MenuItem } from "primeng/api";
import { filter } from "rxjs/operators";
import { UserStorageService } from "@app/core/services/user-storage.service";

@Component({
  selector: "app-content-layout",
  templateUrl: "./content-layout.component.html",
  styleUrls: ["./content-layout.component.scss"],
})
export class ContentLayoutComponent implements OnInit {
  private menuStateKey: string = "menuExpandedGroups";
  isMobile: boolean = window.innerWidth < 1024;
  isStaging: boolean = false;
  items!: MenuItem[];
  sideBarIsOpened: boolean = !this.isMobile;
  faHeart = faHeart;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private userStorageService: UserStorageService,
    private authenticationService: AuthenticationService,
    private router: Router
  ) {}

  setSidebarState(open: boolean | null) {
    if (open === null) {
      this.sideBarIsOpened = !this.sideBarIsOpened;
    } else {
      this.sideBarIsOpened = open;
    }
  }

  ngOnInit() {
    this.items = [
      {
        label: $localize`Home`,
        faIcon: faHome,
        route: ["/dashboard"],
      },
      {
        label: $localize`Attendance`,
        faIcon: faUsers,
        route: ["/time-card"],
        permission: ["time_card.read"],
      },
      {
        label: $localize`Statistics`,
        faIcon: faChartBar,
        route: ["/stat"],
        permission: ["rental.read"],
      },
      {
        label: $localize`Export`,
        faIcon: faCloudDownload,
        route: ["/export"],
        permission: ["export.read"],
      },
      {
        label: $localize`Personal leave`,
        faIcon: faUmbrellaBeach,
        route: ["/personal-leave-request"],
        permission: ["personal_leave_request.read"],
      },
      {
        label: $localize`Customers`,
        faIcon: faUserTie,
        route: ["/customer"],
        permission: ["customer.read"],
      },
      {
        label: $localize`Rentals`,
        faIcon: faBicycle,
        route: ["/rental"],
        permission: ["rental.read"],
      },
      {
        label: $localize`Workshops`,
        faIcon: faWrench,
        route: ["/workshop"],
        permission: ["workshop.read"],
      },
      {
        label: $localize`Places`,
        faIcon: faMapMarkerAlt,
        route: ["/place"],
        permission: ["place.read"],
      },
      {
        id: "issuing-system",
        label: $localize`Issuing system`,
        faIcon: faTicketAlt,
        items: [
          {
            label: $localize`Issues overview`,
            faIcon: faFire,
            route: ["/issue"],
            permission: ["issue.read"],
          },
          {
            label: $localize`Spent time report`,
            faIcon: faClock,
            route: ["/issue-time-log"],
            permission: ["issue.read"],
          },
        ],
      },
      {
        id: "settings",
        label: $localize`Settings`,
        faIcon: faCog,
        items: [
          {
            label: $localize`Users`,
            faIcon: faUsers,
            route: ["/user"],
            permission: ["user.read"],
          },
          {
            label: $localize`Roles`,
            faIcon: faUserShield,
            route: ["/role"],
            permission: ["role.edit"],
          },
          {
            label: $localize`Occupations`,
            faIcon: faBriefcase,
            route: ["/occupation"],
            permission: ["occupation.edit"],
          },
          {
            label: $localize`Operations`,
            faIcon: faTasks,
            route: ["/operation"],
            permission: ["operation.edit"],
          },
          {
            label: $localize`Cars`,
            faIcon: faCar,
            route: ["/car"],
            permission: ["car.edit"],
          },
          {
            label: $localize`Slack city channels`,
            faIcon: faComments,
            route: ["/slack-city-channel"],
            permission: ["slack_city_channel.edit"],
          },
        ],
      },
      {
        id: "vut",
        label: $localize`VUT`,
        faIcon: faBuildingColumns,
        items: [
          {
            // TODO add permission
            label: $localize`Přehledová mapa`,
            faIcon: faMapMarker,
            route: ["/map"],
          },
          {
            label: $localize`Plánovač směny`,
            faIcon: faPen,
            route: ["/input"],
          },
          {
            label: $localize`Navigace`,
            faIcon: faMapLocationDot,
            route: ["/navigation"],
          },
          {
            label: $localize`Itinerář trasy`,
            faIcon: faList,
            route: ["/route-list"],
          },
          {
            label: $localize`New City`,
            faIcon: faCity,
            route: ["/new-city"],
          },
          {
            label: $localize`Task queue`,
            faIcon: faTasks,
            route: ["/comp-taskqueue"],
          },
        ],
      },
    ];

    const hostname = <string>this.document.location.hostname;
    this.isStaging = hostname.startsWith("test-");

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobile) {
          this.setSidebarState(false);
        }
        this.processMenuActiveItems(this.items);
      });

    this.authenticationService.isLoggedIn.subscribe(
      (authorizedUser: AuthorizedUser | null) => {
        this.items = this.processMenuPermissions(this.items);
      }
    );

    this.items = this.processMenuPermissions(this.items);
    this.processMenuActiveItems(this.items);
    this.loadMenuState(this.items);
  }

  processMenuActiveItems(items: MenuItem[]): boolean {
    let anyChildActive = false;

    for (let item of items) {
      // Reset first
      //item.expanded = false;
      item.active = false;

      // If item has children, recurse
      if (item.items) {
        const childActive = this.processMenuActiveItems(item.items);
        if (childActive) {
          item.expanded = childActive;
        }

        anyChildActive = anyChildActive || childActive;
      }

      // Check if this item is directly active
      if (item.route && this.isCurrentRoute(item.route)) {
        item.active = true;
        anyChildActive = true;
      }
    }

    return anyChildActive;
  }

  loadMenuState(items: MenuItem[]) {
    const openGroups = new Set(
      JSON.parse(localStorage.getItem(this.menuStateKey) || "[]")
    );

    for (const item of items) {
      if (item.id && openGroups.has(item.id)) {
        item.expanded = true;
      }
    }
  }

  processMenuPermissions(items: MenuItem[]): MenuItem[] {
    return items
      .map((item) => {
        const hasPermission = item.permission
          ? this.userStorageService.hasPermission(item.permission)
          : true;

        // If the item has children, process them recursively
        if (item.items) {
          item.items = this.processMenuPermissions(item.items);
        }

        // Keep the item only if user has permission AND (either no children or at least one visible child)
        const hasVisibleChildren = !item.items || item.items.length > 0;

        return hasPermission && hasVisibleChildren ? item : null;
      })
      .filter((item) => item !== null) as MenuItem[];
  }

  onPanelExpand(item: MenuItem) {
    if (!item.items || !item.id) {
      // Make sure we are working with group
      return;
    }
    const open = !item.expanded;
    this.toggleGroupExpand(item.id, open);
  }

  toggleGroupExpand(id: string, expanded: boolean) {
    const groups = new Set(
      JSON.parse(localStorage.getItem(this.menuStateKey) || "[]")
    );
    expanded ? groups.add(id) : groups.delete(id);
    localStorage.setItem(this.menuStateKey, JSON.stringify([...groups]));
  }

  isCurrentRoute(routeToCheck: string[]): boolean {
    const urlTree = this.router.createUrlTree(routeToCheck);
    return this.router.isActive(urlTree, {
      paths: "exact",
      queryParams: "ignored",
      matrixParams: "ignored",
      fragment: "ignored",
    });
  }
}
